import { Dispatch, SetStateAction, useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import HistoryIcon from "@mui/icons-material/History";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import { ProfBody } from "../EmployeeCreate/ProfBody";
import { useForm } from "react-hook-form";
import { PersonalBody } from "../EmployeeCreate/PersonalBody";
import { EduBody } from "../EmployeeCreate/EduBody";
import {
  useGetEmployeeByIdQuery,
  useUpdateEmployeeByIdMutation,
} from "entitites/employees/api/employeesApi";
import { Employee } from "entitites/employees/model/employee";
import { PersonalBodyView } from "./PersonalBodyView";
import { useLocation, useNavigate } from "react-router-dom";
import { ProfBodyView } from "./ProfBodyView";
import { EduBodyView } from "./EduBodyView";
import { useConfirm } from "material-ui-confirm";
import { HistoryView } from "./HistoryView";
import { AccountsBody } from "../EmployeeCreate/AccountsBody";
import { AccountsBodyView } from "./AccountsBodyView";
import { AccessesBody } from "entitites/casdoor/ui";
import { useGetMyRolesQuery } from "entitites/casdoor/api/casdoorApi";

export const EmployeeView = () => {
  const [update, data] = useUpdateEmployeeByIdMutation();
  const [isDirty, setIsDirty] = useState(false);
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    data: employee,
    isLoading,
    error,
    isUninitialized,
    isError,
  } = useGetEmployeeByIdQuery(location.pathname.split("/")[2]);
  return (
    <Drawer
      PaperProps={{
        sx: {
          overflow: "auto",
          overflowX: "hidden",
          "::-webkit-scrollbar": {
            display: "none",
          },
        },
      }}
      anchor="right"
      open={location.pathname.includes("edit")}
      onClose={() => {
        if (isDirty) {
          confirm({
            title: "Ты уверен?",
            description: "Есть несохраненные изменения. Выйти?",
            confirmationText: "Выйти",
            cancellationText: "Отмена",
            contentProps: { sx: { pb: 0 } },
          })
            .then(() => {
              navigate(`/${window.location.search}`);
              setIsDirty(false);
            })
            .catch(() => {});
        } else {
          navigate(`/${window.location.search}`);
        }
      }}
    >
      {isError ? (
        <Box m={8} mt={4} minWidth={555}>
          {JSON.stringify(error)}
        </Box>
      ) : isLoading || isUninitialized || data.isLoading ? (
        <Box
          maxWidth={555}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          m={8}
          mt={4}
          gap={1}
        >
          <Box display="flex" gap={1} minHeight={80}>
            <Skeleton>
              <Typography
                maxWidth={490}
                textOverflow="ellipsis"
                overflow="hidden"
                fontWeight={700}
                fontSize={32}
                variant="h4"
              >
                иВАНО ИВАН ИВАНОВИЧ
              </Typography>
            </Skeleton>
            <Box ml="auto">
              <Skeleton variant="circular">
                <IconButton type="submit">
                  <EditIcon sx={{ color: "text.primary" }} />
                </IconButton>{" "}
              </Skeleton>
            </Box>
          </Box>
          <Skeleton>
            <Typography
              fontSize={14}
              display="flex"
              alignItems="center"
              gap={0.5}
              color="#7D7ED0"
              fontWeight={600}
              sx={{ cursor: "pointer" }}
            >
              <HistoryIcon /> История изменений
            </Typography>
          </Skeleton>
          <Box mt={3} gap={1} display="flex" maxWidth={555} flexWrap="wrap">
            <Skeleton>
              <Typography
                fontSize={16}
                fontWeight={600}
                textTransform="uppercase"
              >
                Профессиональная информация
              </Typography>
            </Skeleton>
            <Skeleton>
              <Typography ml="30px" fontSize={16} fontWeight={600}>
                Личная информация
              </Typography>
            </Skeleton>
            <Skeleton>
              <Typography
                fontSize={16}
                fontWeight={600}
                textTransform="uppercase"
              >
                Информация об обучении
              </Typography>
            </Skeleton>
            <Skeleton>
              <Typography
                ml="30px"
                fontSize={16}
                fontWeight={600}
                textTransform="uppercase"
              >
                Учётные записи
              </Typography>
            </Skeleton>
          </Box>
          <Box mt={2}>
            <Box width="100%" display="flex" gap={0.25} flexDirection="column">
              {[
                ["Позиция", "Оператор линии Опозданий"],
                ["Компания", "com"],
                ["Направление", "Направление"],
                ["Отдел", "Клиентский сервис"],
                ["Тимлидер", "Калин Антон"],
                ["График", "7/0"],
                ["Часы работы", "20-01"],
                ["Кто проводил собеседова", "Иванов Антон"],
                ["Внесен в Стажеры", "да"],
              ].map(([first, second], index) => (
                <Box
                  display="flex"
                  alignItems="center"
                  fontWeight={600}
                  width="100%"
                  gap={3}
                  key={index}
                >
                  <Box width={255}>
                    <Skeleton>
                      <Typography>{first}</Typography>
                    </Skeleton>
                  </Box>
                  <Box>
                    <Skeleton>
                      <Typography mr="auto">{second}</Typography>
                    </Skeleton>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      ) : employee ? (
        <EmployeeViewBody
          isDirty={isDirty}
          update={update}
          setIsDirty={setIsDirty}
          employee={data.data || employee}
        />
      ) : (
        <Box m={8} mt={4} minWidth={555}></Box>
      )}
    </Drawer>
  );
};

const EmployeeViewBody = ({
  employee,
  setIsDirty,
  isDirty,
  update,
}: {
  employee: Employee;
  isDirty: boolean;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
  update: any;
}) => {
  const { data: roles } = useGetMyRolesQuery();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [edit, setEdit] = useState(false);
  const { register, handleSubmit, formState, control, watch } = useForm({
    defaultValues: {
      fio: employee.fio,
      profInfo: {
        positionId: employee.profInfo.positionId as unknown as number,
        company: employee.profInfo.company,
        direction: employee.profInfo.direction,
        department: employee.profInfo.department,
        adviser: employee.profInfo.adviser,
        schedule: employee.profInfo.schedule,
        project: employee.profInfo.project,
        workTime: employee.profInfo.workTime,
        additionalFields: employee.profInfo.additionalFields.map((item) => ({
          value: item.value,
          label: item.label,
        })),
      },
      personalInfo: {
        statusId: employee.personalInfo.statusId as unknown as number,
        email: employee.personalInfo.email,
        personalEmail: employee.personalInfo.personalEmail,
        phone: employee.personalInfo.phone,
        personalPhone: employee.personalInfo.personalPhone,
        telegram: employee.personalInfo.telegram,
        city: employee.personalInfo.city,
        dob: employee.personalInfo.dob,
        address: employee.personalInfo.address,
        additionalFields: employee.personalInfo.additionalFields.map(
          (item) => ({ value: item.value, label: item.label })
        ),
      },
      accountsInfo: {
        status: employee.accountsInfo.status,
        appl: employee.accountsInfo.appl,
        accounts: employee.accountsInfo.accounts.map((item) => ({
          value: item.value,
          label: item.label,
        })),
      },
      eduInfo: {
        eduStages: employee.eduInfo.eduStages.map((item) => ({
          dates: item.dates.map((date) => ({
            name: date.name,
            date: date.date,
          })),
          coachFio: item.coachFio,
          position: item.position,
        })),
      },
    },
  });
  useEffect(() => {
    setIsDirty(formState.isDirty);
  }, [formState.isDirty, setIsDirty]);
  return (
    <Box
      maxWidth={555}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      m={8}
      mt={4}
      gap={1}
      component="form"
      onSubmit={handleSubmit((data) => {
        isDirty &&
          Boolean(roles?.includes("staffdesk-admin")) &&
          update({ id: employee.id, body: data });
        setIsDirty(false);
      })}
    >
      <Box display="flex" gap={1} minHeight={80}>
        {edit ? (
          <TextField
            multiline
            sx={{ height: 40, mt: -0.55 }}
            inputProps={{
              ...register("fio", {
                required: "Пожалуйста введите фио",
              }),
              sx: {
                fontSize: 32,
                fontWeight: 700,
                lineHeight: 1.255,
                mt: "3px",
              },
            }}
            InputProps={{ disableUnderline: true }}
            error={Boolean(formState.errors.fio)}
            helperText={formState.errors.fio?.message}
            size="small"
            variant="standard"
          />
        ) : (
          <Typography
            maxWidth={490}
            textOverflow="ellipsis"
            overflow="hidden"
            fontWeight={700}
            fontSize={32}
            variant="h4"
          >
            {employee.fio}
          </Typography>
        )}
        {Boolean(roles?.includes("staffdesk-admin")) && (
          <Box ml="auto">
            {edit ? (
              <IconButton color="success" onClick={() => setEdit(false)}>
                <CheckIcon />
              </IconButton>
            ) : (
              <IconButton type="submit" onClick={() => setEdit(true)}>
                <EditIcon sx={{ color: "text.primary" }} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>
      <Typography
        fontSize={14}
        display="flex"
        alignItems="center"
        gap={0.5}
        color="#7D7ED0"
        fontWeight={600}
        sx={{ cursor: "pointer" }}
        onClick={() =>
          navigate(`edit/${employee.id}/history/${window.location.search}`)
        }
      >
        <HistoryIcon /> История изменений
      </Typography>
      <Box mt={3} gap={1} display="flex" maxWidth={555} flexWrap="wrap">
        <Typography
          onClick={() =>
            navigate(`edit/${employee.id}/prof/${window.location.search}`)
          }
          fontSize={16}
          fontWeight={600}
          textTransform="uppercase"
          sx={{
            transition: "0.15s ease-in-out",
            color:
              pathname.split("/")[3] === "prof"
                ? "text.primary"
                : "text.secondary",
            borderBottom:
              pathname.split("/")[3] === "prof"
                ? "2px solid #9696E290"
                : "2px solid #D9D9D9",
            cursor: "pointer",
          }}
        >
          Профессиональная информация
        </Typography>
        <Typography
          onClick={() =>
            navigate(`edit/${employee.id}/personal/${window.location.search}`)
          }
          ml="30px"
          fontSize={16}
          fontWeight={600}
          textTransform="uppercase"
          sx={{
            transition: "0.15s ease-in-out",
            color:
              pathname.split("/")[3] === "personal"
                ? "text.primary"
                : "text.secondary",
            borderBottom:
              pathname.split("/")[3] === "personal"
                ? "2px solid #9696E290"
                : "2px solid #D9D9D9",
            cursor: "pointer",
          }}
        >
          Личная информация
        </Typography>
        <Typography
          onClick={() =>
            navigate(`edit/${employee.id}/edu/${window.location.search}`)
          }
          fontSize={16}
          fontWeight={600}
          textTransform="uppercase"
          sx={{
            transition: "0.15s ease-in-out",
            color:
              pathname.split("/")[3] === "edu"
                ? "text.primary"
                : "text.secondary",
            borderBottom:
              pathname.split("/")[3] === "edu"
                ? "2px solid #9696E290"
                : "2px solid #D9D9D9",
            cursor: "pointer",
          }}
        >
          Информация об обучении
        </Typography>
        <Typography
          onClick={() =>
            navigate(`edit/${employee.id}/accounts/${window.location.search}`)
          }
          ml="30px"
          fontSize={16}
          fontWeight={600}
          textTransform="uppercase"
          sx={{
            transition: "0.15s ease-in-out",
            color:
              pathname.split("/")[3] === "accounts"
                ? "text.primary"
                : "text.secondary",
            borderBottom:
              pathname.split("/")[3] === "accounts"
                ? "2px solid #9696E290"
                : "2px solid #D9D9D9",
            cursor: "pointer",
          }}
        >
          Учётные записи
        </Typography>
        <Typography
          onClick={() =>
            navigate(`edit/${employee.id}/roles/${window.location.search}`)
          }
          ml="30px"
          fontSize={16}
          fontWeight={600}
          textTransform="uppercase"
          sx={{
            transition: "0.15s ease-in-out",
            color:
              pathname.split("/")[3] === "roles"
                ? "text.primary"
                : "text.secondary",
            borderBottom:
              pathname.split("/")[3] === "roles"
                ? "2px solid #9696E290"
                : "2px solid #D9D9D9",
            cursor: "pointer",
          }}
        >
          Доступы
        </Typography>
      </Box>
      {pathname.includes("personal") && (
        <Box mt={2}>
          {edit ? (
            <PersonalBody
              watch={watch}
              register={register}
              errors={formState.errors}
              control={control}
            />
          ) : (
            <PersonalBodyView employee={employee} />
          )}
        </Box>
      )}
      {pathname.includes("roles") && (
        <Box mt={2}>
          <AccessesBody setIsDirty={setIsDirty} edit={edit} />
        </Box>
      )}
      {pathname.includes("prof") && (
        <Box mt={2}>
          {edit ? (
            <ProfBody
              watch={watch}
              register={register}
              errors={formState.errors}
              control={control}
            />
          ) : (
            <ProfBodyView employee={employee} />
          )}
        </Box>
      )}
      {pathname.includes("edu") && (
        <Box mt={1.5}>
          {edit ? (
            <EduBody register={register} control={control} />
          ) : (
            <EduBodyView employee={employee} />
          )}
        </Box>
      )}
      {pathname.includes("accounts") && (
        <Box mt={2}>
          {edit ? (
            <AccountsBody watch={watch} register={register} control={control} />
          ) : (
            <AccountsBodyView employee={employee} />
          )}
        </Box>
      )}
      {pathname.includes("history") && (
        <Box mt={1}>
          <HistoryView />
        </Box>
      )}
    </Box>
  );
};
